import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import {
    Box, Button, Divider, Grid, List, ListItem, ListItemText, Paper, Snackbar,
    Switch, Typography
} from '@material-ui/core';
import InputText from '../../components/UI/InputText';
import InputLabel from '../../components/UI/InputLabel';
import dateToString from '../../utils/dateToString';

const DEFAULT_TIME_SLOTS = '5-6PM, 8-9PM, 9-10PM';

// The admin fills the days in as YYYY-MM-DD and the deadline as YYYY-MM-DD HH:MM
function parseDeadline(value) {
    if (value.trim().length === 0) return null;
    const deadline = new Date(value.replace(' ', 'T'));
    return isNaN(deadline.getTime()) ? undefined : deadline.getTime();
}

const AdminAvailabilityPage = () => {
    const [forms, setForms] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const getForms = useCallback(async () => {
        const response = await fetch('/api/admin/availability-forms', { credentials: 'include' });
        if (response.status !== 200) return setErrorMessage('Unexpected error');
        const body = (await response.json()).body;
        setForms(body.forms);
    }, []);

    useEffect(() => { getForms(); }, [getForms]);

    const request = useCallback(async (url, method, body) => {
        const response = await fetch(url, {
            method,
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            ...body && { body: JSON.stringify(body) }
        });
        if (response.status !== 200) {
            setErrorMessage('Unexpected error');
            return false;
        }
        const result = await response.json();
        if (result.status === 'FAILED') {
            setErrorMessage(result.body.errorMessages.join(' '));
            return false;
        }
        await getForms();
        return true;
    }, [getForms]);

    return (
        <>
            <Grid container spacing={3} direction='column'>
                <Grid item>
                    <CreateFormCard
                        onCreate={form => request('/api/admin/availability-form', 'POST', form)
                            .then(ok => ok && setSuccessMessage('Availability form created'))}
                        onError={setErrorMessage}
                    />
                </Grid>
                <Grid item>
                    <Box component={Paper} p={2}>
                        <Box mb={3}>
                            <Typography variant='h6'> Availability forms </Typography>
                            <Divider />
                        </Box>
                        {forms.length === 0 && <Typography color='textSecondary'> No availability form yet </Typography>}
                        {forms.map(form => (
                            <FormCard
                                key={form.id}
                                form={form}
                                onToggleOpen={() => request(`/api/admin/availability-form/${form.id}`, 'PUT', { isOpen: !form.isOpen })
                                    .then(ok => ok && setSuccessMessage(form.isOpen ? 'Form closed' : 'Form opened'))}
                                onDelete={() => request(`/api/admin/availability-form/${form.id}`, 'DELETE')
                                    .then(ok => ok && setSuccessMessage('Form deleted'))}
                            />
                        ))}
                    </Box>
                </Grid>
            </Grid>
            <Snackbar open={errorMessage != null} autoHideDuration={4000} onClose={() => setErrorMessage(null)}>
                <Alert onClose={() => setErrorMessage(null)} severity='error'>{errorMessage}</Alert>
            </Snackbar>
            <Snackbar open={successMessage != null} autoHideDuration={2000} onClose={() => setSuccessMessage(null)}>
                <Alert onClose={() => setSuccessMessage(null)} severity='success'>{successMessage}</Alert>
            </Snackbar>
        </>
    );
};

const CreateFormCard = ({ onCreate, onError }) => {
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
    const [deadline, setDeadline] = useState('');
    const [minAvailableSlots, setMinAvailableSlots] = useState('10');
    const [minPreferredSlots, setMinPreferredSlots] = useState('5');

    const onSubmit = () => {
        const parsedDeadline = parseDeadline(deadline);
        if (parsedDeadline === undefined) return onError('The deadline is not a valid date and time');
        onCreate({
            name: name.trim(),
            startDate: startDate.trim(),
            endDate: endDate.trim(),
            timeSlots: timeSlots.split(',').map(slot => slot.trim()).filter(slot => slot.length !== 0),
            deadline: parsedDeadline,
            minAvailableSlots: +minAvailableSlots,
            minPreferredSlots: +minPreferredSlots,
            isOpen: false
        });
    };

    return (
        <Box component={Paper} p={2}>
            <Box mb={3}>
                <Typography variant='h6'> New availability form </Typography>
                <Divider />
            </Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <InputLabel> Name </InputLabel>
                    <InputText value={name} onChange={event => setName(event.target.value)} placeholder='Round 1 availability' />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputLabel> Soft deadline (YYYY-MM-DD HH:MM, optional) </InputLabel>
                    <InputText value={deadline} onChange={event => setDeadline(event.target.value)} placeholder='2026-08-10 23:59' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputLabel> First day (YYYY-MM-DD) </InputLabel>
                    <InputText value={startDate} onChange={event => setStartDate(event.target.value)} placeholder='2026-08-10' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputLabel> Last day (YYYY-MM-DD, at most 7 days) </InputLabel>
                    <InputText value={endDate} onChange={event => setEndDate(event.target.value)} placeholder='2026-08-16' />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputLabel> Time slots, comma separated </InputLabel>
                    <InputText value={timeSlots} onChange={event => setTimeSlots(event.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputLabel> Minimum available slots </InputLabel>
                    <InputText value={minAvailableSlots} onChange={event => setMinAvailableSlots(event.target.value)} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <InputLabel> Minimum preferred slots </InputLabel>
                    <InputText value={minPreferredSlots} onChange={event => setMinPreferredSlots(event.target.value)} />
                </Grid>
            </Grid>
            <Box mt={3}>
                <Button variant='contained' color='primary' onClick={onSubmit}> Create form </Button>
            </Box>
        </Box>
    );
};

const FormCard = ({ form, onToggleOpen, onDelete }) => (
    <Box mb={3}>
        <Box display='flex' alignItems='center' flexWrap='wrap'>
            <Box mr='auto'>
                <Typography variant='subtitle1'>{form.name}</Typography>
                <Typography variant='body2' color='textSecondary'>
                    {`${form.startDate} to ${form.endDate} · ${form.timeSlots.join(', ')}`}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                    {`Deadline: ${form.deadline ? dateToString(form.deadline) : 'none'} · `}
                    {`at least ${form.minAvailableSlots} available and ${form.minPreferredSlots} preferred · `}
                    {`${form.responseCount} response${form.responseCount === 1 ? '' : 's'}`}
                </Typography>
            </Box>
            <Box display='flex' alignItems='center'>
                <Typography variant='body2'>{form.isOpen ? 'Open' : 'Closed'}</Typography>
                <Switch checked={form.isOpen} onChange={onToggleOpen} color='primary' />
                {/* scheduling only makes sense once nobody can change their answer */}
                <Button
                    color='primary'
                    disabled={form.isOpen}
                    component={Link}
                    to={`/availability/${form.id}/schedule`}
                >
                    Schedule matches
                </Button>
                <Button color='secondary' onClick={onDelete}> Delete </Button>
            </Box>
        </Box>
        <List dense>
            {form.remarks.length === 0 ?
                <ListItem>
                    <ListItemText secondary='No remark' />
                </ListItem>
                :
                form.remarks.map(remark => (
                    <ListItem key={remark.username} divider>
                        <ListItemText primary={`${remark.username}: ${remark.remark}`} />
                    </ListItem>
                ))
            }
        </List>
    </Box>
);

export default AdminAvailabilityPage;
